
import React, { useState } from 'react';
import { MemberModel, SiteSettingModel, BranchModel, GRADE_TYPES } from '../../../models';
import { User, Mail, GraduationCap, Building2, Smartphone, MapPin, Save, Edit2, X, ShieldCheck, Search } from 'lucide-react';
import NeisSearchModal from '../../../components/NeisSearchModal';
import Header from './studentFrame/header';

interface StudentProfileProps {
    member: MemberModel;
    siteSettings: SiteSettingModel;
    branches: BranchModel[];
    onUpdate?: (updated: MemberModel) => void;
    isReadOnly?: boolean;
}

const StudentProfile: React.FC<StudentProfileProps> = ({ member, siteSettings, branches, onUpdate, isReadOnly = false }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isSchoolModalOpen, setIsSchoolModalOpen] = useState(false);
    const [formData, setFormData] = useState<MemberModel>({ ...member });

    // Sync formData with member when entering edit mode
    const handleEditClick = () => {
        setFormData({ ...member });
        setIsEditing(true);
    };

    const handleSave = () => {
        if (onUpdate) onUpdate(formData);
        setIsEditing(false);
    };

    const renderInfoItem = ({ icon: Icon, label, value, isEditable = false, field }: { icon: any, label: string, value: any, isEditable?: boolean, field?: keyof MemberModel }) => (
        <div key={label} className="flex flex-col gap-2 p-4 bg-white rounded-2xl border border-slate-50 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-center gap-2 text-slate-400">
                <Icon size={14} />
                <span className="text-[10px] font-black uppercase tracking-widest">{label}</span>
            </div>
            {isEditing && !isReadOnly && isEditable && field ? (
                field === 'gender' ? (
                    <div className="flex gap-2">
                        {(['남', '여'] as const).map((g) => (
                            <button
                                key={g}
                                onClick={() => setFormData(prev => ({ ...prev, gender: g }))}
                                className={`flex-1 py-1.5 rounded-lg text-xs font-bold border transition-all ${formData.gender === g ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-slate-200 text-slate-600'}`}
                            >
                                {g}
                            </button>
                        ))}
                    </div>
                ) : field === 'grade' && siteSettings?.grades ? (
                    <select
                        value={formData.grade || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                        className="w-full text-sm font-bold text-slate-800 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                        <option value="">학년 선택</option>
                        {(siteSettings.grades && siteSettings.grades.length > 0 ? siteSettings.grades : GRADE_TYPES).map((g: string) => (
                            <option key={g} value={g}>{g}</option>
                        ))}
                    </select>
                ) : field === 'schoolName' ? (
                    <div className="relative">
                        <input
                            type="text"
                            value={formData.schoolName || ''}
                            readOnly
                            onClick={() => setIsSchoolModalOpen(true)}
                            className="w-full text-sm font-bold text-slate-800 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                            placeholder="학교 검색"
                        />
                        <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
                    </div>
                ) : (
                    <input
                        type="text"
                        value={formData[field] as string || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, [field]: e.target.value }))}
                        className="w-full text-sm font-bold text-slate-800 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                )
            ) : (
                <p className="text-sm font-bold text-slate-800 break-all">{value || '-'}</p>
            )}
        </div>
    );

    return (
        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 relative">
            {/* Header Section */}
            <Header
                icon={User}
                title="프로필 설정"
                subtitle="회원님의 기본 정보를 관리합니다."
                rightElement={!isReadOnly ? (
                    <div className="flex items-center gap-2">
                        {isEditing ? (
                            <>
                                <button
                                    onClick={() => { setIsEditing(false); setFormData({ ...member }); }}
                                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-white border border-slate-200 rounded-2xl text-slate-600 font-black text-xs hover:bg-slate-50 transition-all active:scale-95 whitespace-nowrap"
                                >
                                    <X size={16} /> 취소
                                </button>
                                <button
                                    onClick={handleSave}
                                    className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-2xl font-black text-xs shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 whitespace-nowrap"
                                >
                                    <Save size={16} /> 저장하기
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={handleEditClick}
                                className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3 bg-slate-900 text-white rounded-2xl font-black text-xs shadow-lg shadow-slate-200 hover:bg-slate-800 transition-all active:scale-95 whitespace-nowrap"
                            >
                                <Edit2 size={16} /> 프로필 수정
                            </button>
                        )}
                    </div>
                ) : undefined}
            />

            {/* Content Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {renderInfoItem({ icon: User, label: "이름", value: member.name })}
                {renderInfoItem({ icon: GraduationCap, label: "학년", value: member.grade, isEditable: true, field: "grade" })}
                {renderInfoItem({ icon: Building2, label: "학교", value: member.schoolName, isEditable: true, field: "schoolName" })}
                {renderInfoItem({ icon: Smartphone, label: "연락처", value: member.phone, isEditable: true, field: "phone" })}
                {renderInfoItem({ icon: MapPin, label: "주소", value: member.address, isEditable: true, field: "address" })}
                {renderInfoItem({ icon: Mail, label: "이메일", value: member.email || '-' })}
                {renderInfoItem({ icon: User, label: "성별", value: member.gender, isEditable: true, field: "gender" })}
                {renderInfoItem({ icon: ShieldCheck, label: "권한", value: member.role === 'student' ? '학생' : member.role })}

            </div>



            {isSchoolModalOpen && (
                <NeisSearchModal
                    onClose={() => setIsSchoolModalOpen(false)}
                    onSelect={(school) => {
                        setFormData({
                            ...formData,
                            schoolName: school.SCHUL_NM,
                            neisSchoolCode: school.SD_SCHUL_CODE,
                            neisSchoolType: school.SCHUL_KND_SC_NM,
                            neisOfficeCode: school.ATPT_OFCDC_SC_CODE,
                            neisOfficeName: school.ATPT_OFCDC_SC_NM,
                        });
                        setIsSchoolModalOpen(false);
                    }}
                />
            )}
        </div>
    );
};

export default StudentProfile;
